import { useState } from "react";
import { HelpCircle } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Link } from "react-router-dom";
import { useAnswerQuestion, useGkQuestions } from "@/features/rfq/api/proceedings";

/**
 * Pytania oferentów oczami GK — pełna treść i firma (dla nas jawne, dla innych firm nie).
 * Odpowiedź trafia prywatnie tylko do pytającej firmy.
 */

export function QuestionsGk({
  proceedingId,
  canAnswer,
}: {
  proceedingId: string;
  canAnswer: boolean;
}) {
  const { data } = useGkQuestions(proceedingId);
  const answer = useAnswerQuestion(proceedingId);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [publicDrafts, setPublicDrafts] = useState<Record<string, boolean>>({});

  const list = data ?? [];

  return (
    <div className="bg-white p-4 dark:bg-gray-800">
      {list.length === 0 ? (
        <p className="text-sm text-[var(--color-text-muted)]">Brak pytań.</p>
      ) : (
        <ul className="space-y-4">
          {list.map((q) => (
            <li key={q.id} className="border-l-2 border-[var(--color-border)] pl-3">
              <div className="flex flex-wrap items-center gap-2">
                <HelpCircle size={13} className="text-[var(--color-text-muted)]" />
                <Link
                  to={`/contractors/${q.contractor}`}
                  className="text-sm font-medium text-[var(--color-primary)] hover:underline"
                >
                  {q.contractor_name}
                </Link>
                {q.answer ? (
                  <Badge color="green">Odpowiedziane</Badge>
                ) : (
                  <Badge color="yellow">Czeka</Badge>
                )}
                {q.is_public && <Badge color="blue">Udostępnione (anonimowo)</Badge>}
                <span className="text-xs text-[var(--color-text-muted)]">
                  {new Date(q.created_at).toLocaleString("pl")}
                </span>
              </div>
              <p className="mt-1 whitespace-pre-wrap text-sm text-[var(--color-text-primary)]">
                {q.body}
              </p>

              {q.answer ? (
                <p className="mt-1 whitespace-pre-wrap border-l-2 border-[var(--color-primary)] pl-2 text-sm text-[var(--color-text-secondary)]">
                  {q.answer}
                  <span className="mt-0.5 block text-xs text-[var(--color-text-muted)]">
                    {q.answered_by_name} ·{" "}
                    {q.answered_at && new Date(q.answered_at).toLocaleString("pl")}
                  </span>
                </p>
              ) : (
                canAnswer && (
                  <div className="mt-2 space-y-2">
                    <textarea
                      value={drafts[q.id] ?? ""}
                      onChange={(e) => setDrafts((d) => ({ ...d, [q.id]: e.target.value }))}
                      rows={2}
                      placeholder="Odpowiedź trafi tylko do tej firmy (chyba że upublicznisz)."
                      className="w-full border border-[var(--color-border)] bg-transparent px-3 py-2 text-sm text-[var(--color-text-primary)]"
                    />
                    <label className="flex items-start gap-2 text-xs text-[var(--color-text-secondary)]">
                      <input
                        type="checkbox"
                        className="mt-0.5 shrink-0"
                        checked={publicDrafts[q.id] ?? false}
                        onChange={(e) =>
                          setPublicDrafts((d) => ({ ...d, [q.id]: e.target.checked }))
                        }
                      />
                      <span>
                        Udostępnij tę parę pytanie-odpowiedź wszystkim oferentom —{" "}
                        <strong>anonimowo</strong> (bez ujawniania, kto pytał: ani osoby,
                        ani firmy).
                      </span>
                    </label>
                    <Button
                      onClick={() =>
                        answer.mutate(
                          {
                            id: q.id,
                            answer: (drafts[q.id] ?? "").trim(),
                            make_public: publicDrafts[q.id] ?? false,
                          },
                          {
                            onSuccess: () => {
                              setDrafts((d) => ({ ...d, [q.id]: "" }));
                              setPublicDrafts((d) => ({ ...d, [q.id]: false }));
                            },
                          },
                        )
                      }
                      disabled={answer.isPending || !(drafts[q.id] ?? "").trim()}
                    >
                      {publicDrafts[q.id]
                        ? "Odpowiedz i udostępnij wszystkim"
                        : "Odpowiedz prywatnie"}
                    </Button>
                  </div>
                )
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
